<template>
  <div class="container-fluid" style="margin: -25px -20px -25px -25px;">
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header bg-primary text-white">
            <h5 class="mb-0">
              <i class="fa fa-file-text-o me-2"></i>
              Detalle de Solicitud de Contratación
              <span class="float-end">
                <BBadge :variant="getEstadoVariant(solicitud?.estado)" class="fs-6">
                  {{ formatEstado(solicitud?.estado) }}
                </BBadge>
              </span>
            </h5>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Cargando...</span>
              </div>
              <p class="mt-2">Cargando información de la solicitud...</p>
            </div>
            
            <div v-else-if="solicitud" style="font-size:small;">
              <!-- TABS PRINCIPALES con v-model para controlar pestaña activa -->
              <b-tabs 
                v-model="activeTab" 
                pills 
                card
                @update:model-value="onTabChange"
              >
                <!-- TAB 1: INFORMACIÓN GENERAL -->
                <b-tab title="Resumen" title-item-class="tab-resumen">
                  <template #title>
                    Resumen<br>General
                  </template>
                  <InformacionGeneral :solicitud="solicitud" />
                </b-tab>

                <!-- TAB 2: DETALLE DE LA SOLICITUD -->
                <b-tab title="Detalle" title-item-class="tab-detalle">
                  <template #title>
                    Detalle <br>
                    <span v-if="solicitud.detalles?.length" class="badge bg-success ms-2">{{ solicitud.detalles.length }} Item(s)</span>
                    <span v-else class="badge bg-warning ms-2">Pendiente</span>
                  </template>
                  <DetalleSolicitud 
                    :solicitud="solicitud"
                    :can-edit="canEditDetalles"
                    @detalle-added="onDetalleAdded"
                    @detalle-updated="onDetalleUpdated"
                    @detalle-deleted="onDetalleDeleted"
                    @refresh="handleRefresh"
                  />
                </b-tab>

                <!-- TAB 3: CERTIFICACIÓN -->
                <b-tab title="Certificación" title-item-class="tab-certificacion">
                  <template #title>
                    Certificación<br>
                    <span v-if="solicitud.certificacion" class="badge bg-success ms-2">Completada</span>
                    <span v-else-if="solicitud.estado === 'EN_CERTIFICACION'" class="badge bg-warning ms-2">Pendiente</span>
                  </template>
                  <EtapaCertificacion 
                    :solicitud="solicitud"
                    :can-certify="canCertificar"
                    @certificado="onCertificado"
                    @refresh="handleRefresh"
                  />
                </b-tab>

                <!-- TAB 4: AUTORIZACIÓN RPA -->
                <b-tab title="Autorización RPA" title-item-class="tab-autorizacion">
                  <template #title>
                    Autorización <br>
                    <span v-if="solicitud.estado_aprobacion === 'EN_AUTORIZACION_RPA'" class="badge bg-warning ms-2">Pendiente</span>
                    <span v-else-if="solicitud.estado_aprobacion === 'AUTORIZADA'" class="badge bg-success ms-2">Aprobada</span>
                    <span v-else-if="solicitud.estado_aprobacion === 'RECHAZADA'" class="badge bg-danger ms-2">Rechazada</span>
                  </template>
                  <EtapaAutorizacion 
                    :solicitud="solicitud"
                    :can-authorize="canAutorizar"
                    @autorizado="onAutorizado"
                    @refresh="handleRefresh"
                  />
                </b-tab>

                <!-- TAB 5: ADJUDICACIÓN -->
                <b-tab title="Adjudicación" title-item-class="tab-adjudicacion">
                  <template #title>
                    Adjudicación<br>
                    <span v-if="solicitud.adjudicacion" class="badge bg-success ms-2">Registrada</span>
                    <span v-else-if="solicitud.estado === 'EN_ADJUDICACION'" class="badge bg-warning ms-2">Pendiente</span>
                  </template>
                  <EtapaAdjudicacion 
                    :solicitud="solicitud"
                    :can-adjudicate="canAdjudicar"
                    @adjudicacion-registrada="onAdjudicacionRegistrada"
                    @refresh="handleRefresh"
                  />
                </b-tab>

                <!-- TAB 6: CONTRATO -->
                <b-tab title="Contrato" title-item-class="tab-contrato">
                  <template #title>
                    Contrato<br>
                    <span v-if="solicitud.contratos?.length" class="badge bg-success ms-2">{{ solicitud.contratos.length }} registro(s)</span>
                    <span v-else-if="solicitud.estado === 'EN_CONTRATACION'" class="badge bg-warning ms-2">Pendiente</span>
                  </template>
                  <EtapaContrato 
                    :solicitud="solicitud"
                    :can-contract="canContratar"
                    @contrato-registrado="onContratoRegistrado"
                    @refresh="handleRefresh"
                  />
                </b-tab>

                <!-- TAB 7: RECEPCIÓN -->
                <b-tab title="Recepción" title-item-class="tab-recepcion">
                  <template #title>
                    Recepción<br>
                    <span v-if="solicitud.recepciones?.length" class="badge bg-success ms-2">{{ solicitud.recepciones.length }} registro(s)</span>
                    <span v-else-if="solicitud.estado === 'CONTRATADA'" class="badge bg-warning ms-2">Pendiente</span>
                  </template>
                  <EtapaRecepcion 
                    :solicitud="solicitud"
                    :can-receive="canRecepcionar"
                    @recepcion-registrada="onRecepcionRegistrada"
                    @refresh="handleRefresh"
                  />
                </b-tab>

                <!-- TAB 8: PAGOS -->
                <b-tab title="Pago" title-item-class="tab-pago">
                  <template #title>
                    Pago<br>
                    <span v-if="solicitud.pagos?.length" class="badge bg-success ms-2">{{ solicitud.pagos.length }} registro(s)</span>
                    <span v-else-if="solicitud.estado === 'EN_PAGO'" class="badge bg-warning ms-2">Pendiente</span>
                  </template>
                  <EtapaPago 
                    :solicitud="solicitud"
                    :can-receive="canPago"
                    @pago-registrada="onPagoRegistrada"
                    @refresh="handleRefresh"
                  />
                </b-tab>

                <!-- TAB 9: INFORME CONCLUSIÓN -->
                <b-tab title="Informe Conclusión" title-item-class="tab-conclusion">
                  <template #title>
                    Conclusión<br>
                    <span v-if="solicitud.informe_conclusion" class="badge bg-success ms-2">Completado</span>
                    <span v-else-if="solicitud.estado === 'PAGADO'" class="badge bg-warning ms-2">Pendiente</span>
                  </template>
                  <EtapaInformeConclusion 
                    :solicitud="solicitud"
                    :can-conclude="canConcluir"
                    @informe-registrado="onInformeRegistrado"
                    @refresh="handleRefresh"
                  />
                </b-tab>
              </b-tabs>

              <!-- Botones de acción globales -->
              <div class="row mt-4">
                <div class="col-md-12 text-end">
                  <BButton variant="secondary" @click="goBack" class="me-2">
                    <i class="fa fa-arrow-left"></i> Volver
                  </BButton>
                  
                  <BButton v-if="canEditSolicitud" variant="warning" @click="editSolicitud" class="me-2">
                    <i class="fa fa-edit"></i> Editar Solicitud
                  </BButton>
                  
                  <BButton 
                    v-if="canSendToCertificacion" 
                    variant="success" 
                    @click="enviarCertificacion" 
                    :disabled="!hasDetalles || sending"
                    class="me-2"
                  >
                    <span v-if="sending" class="spinner-border spinner-border-sm me-2"></span>
                    <i class="fa fa-paper-plane me-1"></i> Enviar a Certificación
                  </BButton>
                  
                  <BButton 
                    v-if="canSendToAuthorization" 
                    variant="success" 
                    @click="enviarAutorizacion" 
                    :disabled="!hasDetalles || sending"
                    class="me-2"
                  >
                    <span v-if="sending" class="spinner-border spinner-border-sm me-2"></span>
                    <i class="fa fa-paper-plane me-1"></i> Enviar a Autorización
                  </BButton>

                  <BButton 
                    v-if="canSendToPago" 
                    variant="success" 
                    @click="enviarPago" 
                    :disabled="!hasDetalles || sending"
                    class="me-2"
                  >
                    <span v-if="sending" class="spinner-border spinner-border-sm me-2"></span>
                    <i class="fa fa-paper-plane me-1"></i> Enviar a Pago
                  </BButton>

                  <BButton 
                    v-if="canSendToFinPago" 
                    variant="success" 
                    @click="finalizarPago" 
                    :disabled="!hasDetalles || sending"
                    class="me-2"
                  >
                    <span v-if="sending" class="spinner-border spinner-border-sm me-2"></span>
                    <i class="fa fa-paper-plane me-1"></i> finalizar a Pago
                  </BButton>
                </div>
              </div>
              
              <!-- Mensajes de advertencia -->
              <div v-if="canSendToCertificacion && !hasDetalles" class="row mt-2">
                <div class="col-md-12">
                  <div class="alert alert-warning text-center">
                    <i class="fa fa-exclamation-triangle me-2"></i>
                    Debe agregar al menos un ítem al detalle antes de enviar a certificación
                  </div>
                </div>
              </div>
              <div v-if="canSendToAuthorization && !hasDetalles" class="row mt-2">
                <div class="col-md-12">
                  <div class="alert alert-warning text-center">
                    <i class="fa fa-exclamation-triangle me-2"></i>
                    Debe agregar al menos un ítem al detalle antes de enviar a autorización
                  </div>
                </div>
              </div>
            </div>
            
            <div v-else class="alert alert-danger">
              No se pudo cargar la información de la solicitud
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import Swal from 'sweetalert2';
import { BButton, BBadge } from 'bootstrap-vue-3';

// Importar componentes de cada etapa
import InformacionGeneral from './InformacionGeneral.vue';
import DetalleSolicitud from './DetalleSolicitud.vue';
import EtapaAutorizacion from './etapas/EtapaAutorizacion.vue';
import EtapaCertificacion from './etapas/EtapaCertificacion.vue';
import EtapaSeleccion from './etapas/EtapaSeleccion.vue';
import EtapaAdjudicacion from './etapas/EtapaAdjudicacion.vue';
import EtapaContrato from './etapas/EtapaContrato.vue';
import EtapaRecepcion from './etapas/EtapaRecepcion.vue';
import EtapaPago from './etapas/EtapaPago.vue';
import EtapaInformeConclusion from './etapas/EtapaConclusion.vue';

const route = useRoute();
const router = useRouter();
const solicitud = ref(null);
const loading = ref(true);
const sending = ref(false);

// Control de pestaña activa - se guarda en sessionStorage para mantener entre recargas
const activeTab = ref(0);

// Mapeo de pestañas por nombre para mantener consistencia
const tabMap = {
  'resumen': 0,
  'detalle': 1,
  'certificacion': 2,
  'autorizacion': 3,
  'adjudicacion': 4,
  'contrato': 5,
  'recepcion': 6,
  'pago':7,
  'conclusion': 8
};

// Guardar pestaña activa cuando cambia
const onTabChange = (tabIndex) => {
  activeTab.value = tabIndex;
  // Opcional: guardar en sessionStorage para mantener entre recargas
  sessionStorage.setItem(`solicitud_tab_${route.params.id}`, tabIndex);
};

// Restaurar pestaña activa desde sessionStorage
const restoreActiveTab = () => {
  const savedTab = sessionStorage.getItem(`solicitud_tab_${route.params.id}`);
  if (savedTab !== null && !isNaN(parseInt(savedTab))) {
    activeTab.value = parseInt(savedTab);
  } else {
    // Determinar pestaña según el estado de la solicitud
    activeTab.value = determineTabByState();
  }
};

// Determinar pestaña inicial basada en el estado de la solicitud
const determineTabByState = () => {
  if (!solicitud.value) return 0;
  
  const estado = solicitud.value.estado;
  
  if (estado === 'EN_CERTIFICACION' || estado === 'CERTIFICADA') {
    return tabMap.certificacion;
  }
  if (estado === 'EN_AUTORIZACION_RPA' || estado === 'AUTORIZADA' || estado === 'RECHAZADA') {
    return tabMap.autorizacion;
  }
  if (estado === 'EN_ADJUDICACION' || estado === 'ADJUDICADA') {
    return tabMap.adjudicacion;
  }
  if (estado === 'EN_CONTRATACION' || estado === 'CONTRATADA') {
    return tabMap.contrato;
  }
  if (estado === 'EN_RECEPCION' || estado === 'RECIBIDA') {
    return tabMap.recepcion;
  }
  if (estado === 'EN_PAGO' || estado === 'PAGADO') {
    return tabMap.pago;
  }
  if (estado === 'CONCLUIDA') {
    return tabMap.conclusion;
  }
  return tabMap.detalle;
};

// Computed para verificar si tiene detalles
const hasDetalles = computed(() => {
  return solicitud.value?.detalles?.length > 0;
});

const canSendToCertificacion = computed(() => {
  if (!solicitud.value) return false;
  const estado = solicitud.value.estado;
  return estado === 'BORRADOR' && hasDetalles.value;
});

const canSendToAuthorization = computed(() => {
  if (!solicitud.value) return false;
  const estado = solicitud.value.estado;
  return estado === 'CERTIFICADA' && hasDetalles.value;
});

const canSendToPago = computed(() => {
  if (!solicitud.value) return false;
  const estado = solicitud.value.estado;
  return estado === 'RECIBIDA' && hasDetalles.value;
});
const canSendToFinPago = computed(() => {
  if (!solicitud.value) return false;
  const estado = solicitud.value.estado;
  return estado === 'EN_PAGO' && hasDetalles.value;
});

const canEditSolicitud = computed(() => {
  return solicitud.value && ['BORRADOR', 'RECHAZADA'].includes(solicitud.value.estado);
});

const canEditDetalles = computed(() => {
  return solicitud.value && solicitud.value.estado === 'BORRADOR';
});

const canEditPago = computed(() => {
  return solicitud.value && solicitud.value.estado === 'EN_PAGO';
});
const canAutorizar = computed(() => {
  return solicitud.value && solicitud.value.estado === 'EN_AUTORIZACION_RPA';
});

const canCertificar = computed(() => {
  return solicitud.value && solicitud.value.estado === 'EN_CERTIFICACION';
});

const canSeleccionar = computed(() => {
  return solicitud.value && solicitud.value.estado === 'EN_SELECCION';
});

const canAdjudicar = computed(() => {
  return solicitud.value && solicitud.value.estado === 'EN_ADJUDICACION';
});

const canContratar = computed(() => {
  return solicitud.value && solicitud.value.estado === 'EN_CONTRATACION';
});

const canRecepcionar = computed(() => {
  return solicitud.value && solicitud.value.estado === 'EN_RECEPCION';
});

const canPago = computed(() => {
  return solicitud.value && solicitud.value.estado === 'EN_PAGO';
});

const canConcluir = computed(() => {
  return solicitud.value && solicitud.value.estado === 'PAGADO';
});

const loadSolicitud = async (keepTab = true) => {
  loading.value = true;
  try {
    const response = await axios.get(`/solicitud-contratacion/mostrar/${route.params.id}`);
    solicitud.value = response.data.data;
    
    // Restaurar o determinar pestaña activa después de cargar
    if (!keepTab) {
      // Si es una recarga completa, determinar por estado
      activeTab.value = determineTabByState();
    }
  } catch (error) {
    console.error('Error loading solicitud:', error);
    Swal.fire('Error', 'No se pudo cargar la solicitud', 'error');
  } finally {
    loading.value = false;
  }
};

// Handler para refrescar después de cualquier acción, manteniendo la pestaña actual
const handleRefresh = async (targetTab = null) => {
  const currentTab = targetTab !== null ? targetTab : activeTab.value;
  await loadSolicitud(true);
  // Asegurar que la pestaña se mantiene
  activeTab.value = currentTab;
};

const goBack = () => {
  router.push({ name: 'solicitud-contratacion' });
};

const editSolicitud = () => {
  router.push({ name: 'solicitud-contratacion-edit', params: { id: solicitud.value.id } });
};

const enviarCertificacion = async () => {
  if (!hasDetalles.value) {
    Swal.fire('Error', 'Debe agregar al menos un ítem al detalle antes de enviar a certificación', 'warning');
    return;
  }
  
  if (solicitud.value.estado !== 'BORRADOR') {
    Swal.fire('Error', 'La solicitud ya fue enviada a certificación o está en un estado que no permite esta acción', 'warning');
    return;
  }
  
  const result = await Swal.fire({
    title: '¿Enviar a certificación?',
    text: `La solicitud ${solicitud.value.codigo} será enviada para certificación.`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Sí, enviar',
    cancelButtonText: 'Cancelar'
  });
  
  if (result.isConfirmed) {
    sending.value = true;
    try {
      await axios.post(`/solicitud-contratacion/enviar-certificacion/${solicitud.value.id}`);
      Swal.fire('Éxito', 'Solicitud enviada a certificación', 'success');
      // Mantener en la pestaña de certificación después del envío
      await handleRefresh(tabMap.certificacion);
    } catch (error) {
      const message = error.response?.data?.message || 'Error al enviar la solicitud';
      Swal.fire('Error', message, 'error');
    } finally {
      sending.value = false;
    }
  }
};

const enviarAutorizacion = async () => {
  if (!hasDetalles.value) {
    Swal.fire('Error', 'Debe agregar al menos un ítem al detalle antes de enviar a autorización', 'warning');
    return;
  }
  
  if (solicitud.value.estado !== 'CERTIFICADA') {
    Swal.fire('Error', 'La solicitud ya fue enviada a autorización o está en un estado que no permite esta acción', 'warning');
    return;
  }
  
  const result = await Swal.fire({
    title: '¿Enviar a autorización?',
    text: `La solicitud ${solicitud.value.codigo} será enviada al RPA para autorización de inicio.`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Sí, enviar',
    cancelButtonText: 'Cancelar'
  });
  
  if (result.isConfirmed) {
    sending.value = true;
    try {
      await axios.post(`/solicitud-contratacion/enviar-autorizacion/${solicitud.value.id}`);
      Swal.fire('Éxito', 'Solicitud enviada a autorización', 'success');
      // Mantener en la pestaña de autorización después del envío
      await handleRefresh(tabMap.autorizacion);
    } catch (error) {
      const message = error.response?.data?.message || 'Error al enviar la solicitud';
      Swal.fire('Error', message, 'error');
    } finally {
      sending.value = false;
    }
  }
};

const enviarPago = async () => {
  if (!hasDetalles.value) {
    Swal.fire('Error', 'Debe agregar al menos un ítem al detalle antes de enviar a pago', 'warning');
    return;
  }
  
  if (solicitud.value.estado !== 'RECIBIDA') {
    Swal.fire('Error', 'La solicitud ya fue enviada a pago o está en un estado que no permite esta acción', 'warning');
    return;
  }
  
  const result = await Swal.fire({
    title: '¿Enviar a pago?',
    text: `La solicitud ${solicitud.value.codigo} será enviada al contabilidad para el proceso de pago.`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Sí, enviar',
    cancelButtonText: 'Cancelar'
  });
  
  if (result.isConfirmed) {
    sending.value = true;
    try {
      await axios.post(`/solicitud-contratacion/enviar-pago/${solicitud.value.id}`);
      Swal.fire('Éxito', 'Solicitud enviada a pago', 'success');
      // Mantener en la pestaña de autorización después del envío
      await handleRefresh(tabMap.autorizacion);
    } catch (error) {
      const message = error.response?.data?.message || 'Error al enviar la solicitud';
      Swal.fire('Error', message, 'error');
    } finally {
      sending.value = false;
    }
  }
};

const finalizarPago = async () => {
  if (!hasDetalles.value) {
    Swal.fire('Error', 'Debe agregar al menos un pago antes de finalizar a pago', 'warning');
    return;
  }
  
  if (solicitud.value.estado !== 'EN_PAGO') {
    Swal.fire('Error', 'La solicitud ya fue pagado o está en un estado que no permite esta acción', 'warning');
    return;
  }
  
  const result = await Swal.fire({
    title: '¿Finalizar pago?',
    text: `La solicitud ${solicitud.value.codigo} será finalizada el proceso de pago.`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Sí, finalizar',
    cancelButtonText: 'Cancelar'
  });
  
  if (result.isConfirmed) {
    sending.value = true;
    try {
      await axios.post(`/solicitud-contratacion/finalizar-pago/${solicitud.value.id}`);
      Swal.fire('Éxito', 'Pago finalizado correctamente', 'success');
      // Mantener en la pestaña de autorización después del envío
      await handleRefresh(tabMap.autorizacion);
    } catch (error) {
      const message = error.response?.data?.message || 'Error al enviar la solicitud';
      Swal.fire('Error', message, 'error');
    } finally {
      sending.value = false;
    }
  }
};



// Handlers para eventos de los componentes hijos que refrescan manteniendo la pestaña
const onDetalleAdded = () => handleRefresh(tabMap.detalle);
const onDetalleUpdated = () => handleRefresh(tabMap.detalle);
const onDetalleDeleted = () => handleRefresh(tabMap.detalle);
const onAutorizado = () => handleRefresh(tabMap.autorizacion);
const onCertificado = () => handleRefresh(tabMap.certificacion);
const onSeleccionRegistrada = () => handleRefresh(tabMap.adjudicacion);
const onAdjudicacionRegistrada = () => handleRefresh(tabMap.adjudicacion);
const onContratoRegistrado = () => handleRefresh(tabMap.contrato);
const onRecepcionRegistrada = () => handleRefresh(tabMap.recepcion);
const onPagoRegistrada = () => handleRefresh(tabMap.pago);
const onInformeRegistrado = () => handleRefresh(tabMap.conclusion);

// Formateadores
const formatEstado = (estado) => {
  if (!estado) return 'N/A';
  return estado.replace(/_/g, ' ').toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const getEstadoVariant = (estado) => {
  const variants = {
    'BORRADOR': 'secondary',
    'EN_AUTORIZACION_RPA': 'warning',
    'AUTORIZADA': 'success',
    'RECHAZADA': 'danger',
    'EN_CERTIFICACION': 'info',
    'CERTIFICADA': 'info',
    'EN_SELECCION': 'primary',
    'EN_ADJUDICACION': 'warning',
    'ADJUDICADA': 'success',
    'EN_CONTRATACION': 'primary',
    'CONTRATADA': 'success',
    'EN_RECEPCION': 'warning',
    'RECIBIDA': 'dark',
    'EN_PAGO': 'warning',
    'PAGADO': 'dark',
    'CONCLUIDA': 'light'
  };
  return variants[estado] || 'secondary';
};

onMounted(() => {
  loadSolicitud();
  restoreActiveTab();
});

// Watch para cuando cambia la solicitud, ajustar pestaña si es necesario
watch(solicitud, (newVal) => {
  if (newVal) {
    // Si no hay pestaña guardada, determinar por estado
    if (activeTab.value === undefined || activeTab.value === null) {
      activeTab.value = determineTabByState();
    }
  }
});
</script>

<style scoped>
.container-fluid {
  padding: 0px;
}

/* Estilos para mejorar la visibilidad de las pestañas activas */
:deep(.nav-tabs) {
  border-bottom: 1px solid #dee2e6;
  background-color: #f8f9fa;
  border-radius: 0.25rem 0.25rem 0 0;
}

:deep(.nav-tabs .nav-link) {
  color: #495057;
  font-weight: 500;
  padding: 0.25rem;
  border: none;
  border-radius: 0;
  transition: all 0.2s ease-in-out;
}

:deep(.nav-tabs .nav-link:hover) {
  background-color: #e9ecef;
  color: #0d6efd;
  border-color: transparent;
}

:deep(.nav-tabs .nav-link.active) {
  color: #0d6efd !important;
  background-color: #ffffff !important;
  border-bottom: 3px solid #0d6efd !important;
  font-weight: 600;
}

:deep(.nav-tabs .nav-link.active:hover) {
  background-color: #ffffff;
  color: #0a58ca;
}

:deep(.nav-link .badge) {
  margin-left: 0.2rem;
  font-size: 0.65rem;
}

:deep(.nav-link:not(.active)) {
  color: #6c757d;
}

:deep(.nav-link:not(.active)):hover {
  color: #0d6efd;
}

:deep(.nav-pills .nav-link) {
  color: #495057;
  border-radius: 0.375rem;
  margin-right: 0.2rem;
}

:deep(.nav-pills .nav-link.active) {
  background-color: #0d6efd !important;
  color: #ffffff !important;
}

:deep(.nav-pills .nav-link.active .badge) {
  background-color: #ffffff;
  color: #0d6efd;
}

:deep(.card-header) {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

:deep(.card-body) {
  padding: 0.8rem;
}

:deep(.nav-link .badge.bg-warning) {
  color: #000;
}

:deep(.nav-link .badge.bg-success) {
  color: #fff;
}

:deep(.nav-link .badge.bg-danger) {
  color: #fff;
}

:deep(.nav-link .badge.bg-info) {
  color: #fff;
}

@media (max-width: 768px) {
  :deep(.nav-tabs .nav-link) {
    padding: 0.1rem 0.25rem;
    font-size: 0.75rem;
  }
  
  :deep(.nav-tabs .nav-link .badge) {
    font-size: 0.55rem;
    margin-left: 0.1rem;
  }
}
</style>